import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { useLocation } from 'react-router-dom'; // Importar useLocation

const PopupManager = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { settings } = useSettings();
  const [activePopups, setActivePopups] = useState([]);
  const location = useLocation(); // Obtener la ubicación actual

  useEffect(() => {
    // Mostrar popups solo si está autenticado y en la ruta correspondiente
    if (!isAuthenticated) {
      setActivePopups([]);
      return;
    }
    const popupsToShow = (settings.popups || []).filter(popup => 
      popup.active && popup.showOnPages.includes(location.pathname)
    );
    setActivePopups(popupsToShow.map(popup => ({ ...popup, show: false }))); // Inicialmente ocultos
  }, [settings.popups, location.pathname]); // Dependencia de location.pathname para reaccionar a cambios de ruta

  useEffect(() => {
    activePopups.forEach((popup, index) => {
      if (popup.trigger === 'onLoad' && !popup.show) {
        // Simular un pequeño retraso para que la página cargue
        setTimeout(() => {
          setActivePopups(prev => prev.map((p, i) => i === index ? { ...p, show: true } : p));
        }, 1000 * (index + 1)); // Mostrar con un pequeño desfase
      }
    });
  }, [activePopups]);

  const handleClose = (id) => {
    setActivePopups(prev => prev.filter(popup => popup.id !== id));
  };

  return (
    <div className="fixed inset-0 z-[999] pointer-events-none">
      {activePopups.map(popup => (
        <AnimatePresence key={popup.id}>
          {popup.show && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 pointer-events-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-3xl p-6 shadow-2xl max-w-md w-full relative"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: "spring", damping: 20, stiffness: 300 }}
              >
                <button
                  onClick={() => handleClose(popup.id)}
                  className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                  title={t('logout')}
                >
                  <X className="w-5 h-5" />
                </button>
                
                {popup.imageUrl && (
                  <img src={popup.imageUrl} alt={popup.title} className="w-full h-48 object-cover rounded-2xl mb-4" />
                )}

                <h3 className="text-2xl font-bold text-gray-900 mb-3">{popup.title}</h3>
                <p className="text-gray-700 mb-4">{popup.content}</p>

                {popup.buttonText && popup.buttonLink && (
                  <motion.a
                    href={popup.buttonLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {popup.buttonText}
                  </motion.a>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      ))}
    </div>
  );
};

export default PopupManager;